import {
  motion, useTransform, useScroll, useSpring
} from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Image from 'components/Image';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import React, { useRef } from 'react';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

const year = new Date().getFullYear();
const TITLE = 'Let Scribble do the work';
const SUBTITLE = 'Create a coffee table centerpiece with photos straight from your phone';
const BOOK_TITLE = `Stuff we Made ${year}`;
const BOOK_SUBTITLE = 'A book by Michael and Madison';
const BOTTOM_TEXT = 'Create a gift with a unique personal touch!';
const CTA = 'Create your custom book now!';
const ARTWORK_SRC = '/img/marketing/landing/section_ai_art/ai_girl.png';

const AiScroller = ({ ctaClick }) => {
  const ref = useRef(null);
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });

  // AI Girl
  const girlStartScale = isExtraSmall ? 1.2 : 0.8; // It's too small on mobile
  const girlScale = useTransform(scrollYProgress, [0.1, 0.4], [girlStartScale, 1]);
  // const girlScaleSpring = useSpring(girlScale, { stiffness: 50, damping: 3, mass: 3 });
  const rotateGirl = useTransform(scrollYProgress, [0.1, 0.4, 0.6], [40, 0, 0]);

  const topOpacity = useTransform(scrollYProgress, [0, 0.2, 1], [1, 0, 0]);
  const bookOpacity = useTransform(scrollYProgress, [0, 0.4, 0.5], [0, 0, 1]);
  const bookTitleOpacity = useTransform(scrollYProgress, [0, 0.5, 0.6], [0, 0, 1]);
  const bottomTextOpacity = useTransform(scrollYProgress, [0, 0.7, 0.8], [0, 0, 1]);
  const tagY = useTransform(scrollYProgress, [0.7, 0.8], [-300, 0]);
  const tagYSpring = useSpring(tagY, { stiffness: 50, damping: 15, mass: 1 });

  // scrollYProgress.onChange((x) => {});

  return (
    <Stack className="aiScroller" ref={ref}>
      <motion.div
        className="messTop"
        style={{
          opacity: topOpacity
        }}
      />
      <Box
        sx={{
          zIndex: 3
        }}
      >
        <Typography
          variant="bookPreview"
          component="h3"
          sx={{
            mt: 2,
            maxWidth: '90%',
            mx: 'auto'
          }}
        >
          {TITLE}
        </Typography>
        <Typography
          variant="bookPreview"
          component="h4"
          sx={{
            mt: 2,
            maxWidth: '90%',
            mx: 'auto'
          }}
        >
          {SUBTITLE}
        </Typography>
      </Box>
      <Box className="characterBox" mt={15}>
        <motion.div
          className="book"
          style={{
            opacity: bookOpacity
          }}
        />
        <motion.div
          className="aiGirl"
          style={{
            scale: girlScale,
            rotate: rotateGirl
          }}
        >
          <Image
            src={ARTWORK_SRC}
            alt="Scribble Artwork"
            layout="fill"
            objectFit="contain"
            sizes="(min-width: 8750px) 750px,
                   (min-width: 640px) 400px
                  270px"
          />
        </motion.div>
        <motion.div
          className="bookTitle"
          style={{
            opacity: bookTitleOpacity
          }}
        >
          <Typography
            variant="bookPreview"
            component="strong"
          >
            {BOOK_TITLE}
          </Typography>
          <Typography
            variant="bookPreview"
            component="p"
          >
            {BOOK_SUBTITLE}
          </Typography>
        </motion.div>
        <Box className="giftTag">
          <motion.div
            className="tagBox"
            style={{
              y: tagYSpring
            }}
          >
            <Image
              src="/img/marketing/landing/section_ai_art/gift_tag.png"
              alt="Gift tag"
              layout="fill"
              objectFit="contain"
              priority
            />
          </motion.div>
        </Box>
      </Box>
      <motion.div
        className="subBookText"
        style={{
          opacity: bottomTextOpacity
        }}
      >
        <Typography
          variant="bookPreview"
          component="h3"
        >
          {BOTTOM_TEXT}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={ctaClick}
          sx={{ mt: 6 }}
        >
          {CTA}
        </Button>
      </motion.div>
    </Stack>
  );
};

AiScroller.propTypes = {
  ctaClick: PropTypes.func.isRequired
};

export default AiScroller;
